import { Clock, Coins, ListChecks } from 'lucide-react';

export const REVIEW_LANGUAGES = [
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'en-GB', label: 'Anglais (UK)', flag: '🇬🇧' },
  { value: 'de', label: 'Allemand', flag: '🇩🇪' },
  { value: 'es', label: 'Espagnol', flag: '🇪🇸' },
  { value: 'it', label: 'Italien', flag: '🇮🇹' },
  { value: 'nl', label: 'Néerlandais', flag: '🇳🇱' },
];

export const RATING_PRESETS = [
  { value: 'excellent', label: 'Excellent (~4,8 ★)' },
  { value: 'positive', label: 'Positif (~4,5 ★)' },
  { value: 'realistic', label: 'Réaliste (~4,2 ★)' },
  { value: 'mixed', label: 'Mitigé (~3,9 ★)' },
];

export const DEFAULT_FROM = '2025-02-09';
export const todayISO = () => new Date().toISOString().slice(0, 10);

// Paramètres de génération (alignés sur le backend : lots de 15 avis)
const BATCH_SIZE = 15;
const SECONDS_PER_BATCH = 7; // durée moyenne observée d'un appel LLM + insertion
const COST_PER_REVIEW = 0.001; // coût indicatif (USD) par avis généré via Claude Sonnet

const fmtDuration = (seconds) => {
  if (seconds < 60) return `~${Math.max(seconds, 1)}s`;
  const min = Math.round(seconds / 60);
  return `~${min} min`;
};

export const estimateGeneration = (count) => {
  const n = Math.max(0, Math.round(count || 0));
  const batches = Math.ceil(n / BATCH_SIZE);
  return {
    count: n,
    batches,
    durationLabel: fmtDuration(batches * SECONDS_PER_BATCH),
    costLabel: `~$${(n * COST_PER_REVIEW).toFixed(2)}`,
  };
};

// count peut être un nombre (cible fixe) ou [min, max] (cible aléatoire)
export const EstimationPanel = ({ count, idPrefix = '' }) => {
  const isRange = Array.isArray(count);
  const lo = estimateGeneration(isRange ? count[0] : count);
  const hi = isRange ? estimateGeneration(count[1]) : null;

  if (lo.count <= 0 && !(isRange && hi.count > 0)) return null;

  const countText = isRange ? `${lo.count} à ${hi.count}` : `${lo.count}`;
  const durationText = isRange ? `${lo.durationLabel} – ${hi.durationLabel}` : lo.durationLabel;
  const costText = isRange ? `${lo.costLabel} – ${hi.costLabel}` : lo.costLabel;

  return (
    <div className="mb-4 p-3 rounded-lg bg-[#00b67a]/5 border border-[#00b67a]/30" data-testid={`${idPrefix}estimation`}>
      <p className="text-xs font-semibold text-[#00825a] uppercase tracking-wide mb-2">Estimation avant lancement</p>
      <div className="grid grid-cols-3 gap-2">
        <div className="flex flex-col items-start">
          <span className="flex items-center gap-1 text-xs text-gray-500"><ListChecks className="w-3.5 h-3.5" /> Avis</span>
          <span className="text-sm font-bold text-[#1a1a1a]" data-testid={`${idPrefix}est-count`}>{countText}</span>
        </div>
        <div className="flex flex-col items-start">
          <span className="flex items-center gap-1 text-xs text-gray-500"><Clock className="w-3.5 h-3.5" /> Durée</span>
          <span className="text-sm font-bold text-[#1a1a1a]" data-testid={`${idPrefix}est-duration`}>{durationText}</span>
        </div>
        <div className="flex flex-col items-start">
          <span className="flex items-center gap-1 text-xs text-gray-500"><Coins className="w-3.5 h-3.5" /> Coût</span>
          <span className="text-sm font-bold text-[#1a1a1a]" data-testid={`${idPrefix}est-cost`}>{costText}</span>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Estimations indicatives (lots de {BATCH_SIZE} avis). La durée et le coût réels peuvent varier.</p>
    </div>
  );
};

export const DistributionFields = ({ ratingPreset, setRatingPreset, dateFrom, setDateFrom, dateTo, setDateTo, idPrefix = '' }) => {
  return (
    <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg border" data-testid={`${idPrefix}distribution`}>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Tendance des notes</label>
        <select
          value={ratingPreset}
          onChange={(e) => setRatingPreset(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
          data-testid={`${idPrefix}rating-preset`}
        >
          {RATING_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Dates du</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
            data-testid={`${idPrefix}date-from`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">au</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
            data-testid={`${idPrefix}date-to`}
          />
        </div>
      </div>
    </div>
  );
};

export default DistributionFields;
